import React, { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { SplitButton } from 'primereact/splitbutton';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Checkbox } from 'primereact/checkbox';
import { SelectButton } from 'primereact/selectbutton';
import { Tag } from 'primereact/tag';
import { FaBarcode } from 'react-icons/fa';
import { BrowserMultiFormatReader } from '@zxing/browser';
import axiosInstance from '../services/axiosInstance';
import { handleApiError, handleApiSuccess } from '../components/apiErrorHandler';
import { ProductSelectorInput } from './ProductSelectorInput';
import ExpiryInput from './ExpiryInput';
import { toYmd, ymdToDate } from '../utils/dateOnly';
import RecordPaymentDialog from './RecordPaymentDialog';
import { printPurchaseOrder } from './PurchaseOrderPrintView';

export default function MoveOrderPurchaseDialog({ visible, onHide, onPurchased, reinitOrder, editOrderId }) {
  const [items, setItems] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [sendDialogVisible, setSendDialogVisible] = useState(false);
  const [sendEmailTo, setSendEmailTo] = useState('');
  const [receiveDialogVisible, setReceiveDialogVisible] = useState(false);
  const [receiveOnCredit, setReceiveOnCredit] = useState(false);
  const [receiveInvoiceNo, setReceiveInvoiceNo] = useState('');
  const [receiveItemsState, setReceiveItemsState] = useState([]);
  const [receiveSupplierId, setReceiveSupplierId] = useState(null);
  const [paymentDialogVisible, setPaymentDialogVisible] = useState(false);
  const [orgSettings, setOrgSettings] = useState({});
  const [isDirectGrn, setIsDirectGrn] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [grnDialogVisible, setGrnDialogVisible] = useState(false);
  const [grnReceiveOnCredit, setGrnReceiveOnCredit] = useState(true);
  const [grnSelectedSupplier, setGrnSelectedSupplier] = useState(selectedSupplier || null);
  const [grnSupplierInvoiceNo, setGrnSupplierInvoiceNo] = useState('');
  const [grnSubmitting, setGrnSubmitting] = useState(false); 
  const [templateSaveVisible, setTemplateSaveVisible] = useState(false);
  const [templateLoadVisible, setTemplateLoadVisible] = useState(false);
  const [templateFillMode, setTemplateFillMode] = useState(false);
  const [templateMeta, setTemplateMeta] = useState(null); // { id, name }
  const [selectedRowsQuick, setSelectedRowsQuick] = useState([]); // selection in quick-fill table (array of row objects)
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [productQuery, setProductQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [qty, setQty] = useState(null);
  const [pricingMode, setPricingMode] = useState('pack'); // 'pack' | 'unit' — controls single-item entry form
  // The product's own persisted default (Product.pricing_type from the backend), used to
  // detect whether the user has deliberately overridden it for the currently selected product.
  const [productPricingType, setProductPricingType] = useState('pack');
  // "Unit Stocks" — hard override for the whole PO/GRN when entering existing shelf-counted
  // stock. Forces Pack/Unit toggle to Unit and locks it; not compatible with template loading.
  const [unitStocksMode, setUnitStocksMode] = useState(false);
  const [unitPriceUI, setUnitPriceUI] = useState(null); 
  const [unitsPerPack, setUnitsPerPack] = useState(null);
  const [packPrice, setPackPrice] = useState(null);
  const [freeQty, setFreeQty] = useState(0);
  const [purchaseDiscount, setPurchaseDiscount] = useState(0);
  const [rl, setRl] = useState(null);
  const [purchasePrice, setPurchasePrice] = useState(null);
  const [lineTotalCostComputed, setLineTotalCostComputed] = useState(null);
  const [sellingPrice, setSellingPrice] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [batchNumber, setBatchNumber] = useState('');
  const [scannerVisible, setScannerVisible] = useState(false);
  const [userDept, setUserDept] = useState(null);
  const [blockExpiryDays, setBlockExpiryDays] = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierCredit, setSupplierCredit] = useState(null);
  const [supplierCreditDays, setSupplierCreditDays] = useState(null);
  const [supplierEmail, setSupplierEmail] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false); // guards Save/Create against double-submit
  // React state updates are async/batched, leaving a brief window where a fast double-click
  // can slip past the `savingOrder` state check before the button visually disables. This
  // ref updates synchronously, so it closes that window even within the same event tick.
  const savingOrderRef = useRef(false);
  const isCancelled = currentOrder?.status === 'cancelled';
  // A draft order is still fully editable (that's the whole point of Save-as-draft);
  // only lock the form once the order has moved past draft (approved, sent, received, etc).
  const isReadOnlyOrder = !!currentOrder && currentOrder.status !== 'draft';

  // refs
  const productRef = useRef(null);
  const qtyRef = useRef(null);
  const rlRef = useRef(null);
  const uppRef = useRef(null);
  const expiryRef = useRef(null);
  const unitPriceRef = useRef(null);      // NEW
  const supplierRef = useRef(null);
  const sellingPriceRef = useRef(null);
  const freeQtyRef = useRef(null);
  const discountRef = useRef(null);
  const addRef = useRef(null);
  const updateRef = useRef(null);

  const focusRef = (r) => setTimeout(() => r?.current?.focus?.(), 0);

  useEffect(() => {
    axiosInstance.get('/api/suppliers/?all=1').then(resp => setSuppliers(resp.data));
    const s = JSON.parse(localStorage.getItem('organizationSettings') || '{}');
    const userSettings = JSON.parse(localStorage.getItem('userSettings') || '{}');
    setBlockExpiryDays(s.block_invoice_days_before_expiry ?? 0);
    setUserDept(userSettings.department ?? null)
    setOrgSettings(s || {});
    if (!s.allow_direct_grn_auto_approve) {
        setIsDirectGrn(false);
      } else {
        setIsDirectGrn(true);
      }
  }, []);

  // autofocus supplier filter on open; reset form on close
  useEffect(() => {
    if (visible) {
      setTimeout(() => openSupplierAndFocusFilter(), 500);
    } else {
      resetForm(true);
      setItems([]);
      setCurrentOrder(null);
      setTemplateFillMode(false);
      setTemplateMeta(null);
      setSelectedRowsQuick([]);
    }
  }, [visible]);

  useEffect(() => {
    if (visible && reinitOrder && !currentOrder) {
      applyReinitOrder(reinitOrder);
    }
  }, [visible, reinitOrder, currentOrder, suppliers]);

  useEffect(() => {
    if (visible && editOrderId && !currentOrder) {
      loadDraftForEditing(editOrderId);
    }
  }, [visible, editOrderId, currentOrder, suppliers]);

  // Populate the Receive Goods dialog's editable item list from whatever currentOrder is
  // set to, whichever code path set it (create, loadDraftForEditing, refreshCurrentOrder).
  // Matches MoveOrderDetailDialog's pattern — driven declaratively off the order itself
  // rather than duplicated inside every function that might load one.
  useEffect(() => {
    if (currentOrder && Array.isArray(currentOrder.items)) {
      setReceiveItemsState(currentOrder.items.map(it => ({
        move_order_item: it.id,
        product_name: it.product_name,
        units_per_pack: it.units_per_pack || null,
        // Displayed alongside received_quantity/free_quantity below, which are both in
        // packs for pack-mode rows -- convert this the same way so all three columns
        // in the Receive Goods table read in the same unit instead of mixing units
        // (ordered) with packs (received/free), which was confusing to fill in.
        ordered_quantity: it.units_per_pack ? (it.quantity || 0) / it.units_per_pack : (it.quantity || 0),
        // Pack-mode rows edit received_quantity in PACKS (matching free_quantity's
        // existing conversion right below, and the create-flow's pack_quantity
        // convention) -- the backend's /receive endpoint expects both received_quantity
        // and free_quantity in packs for pack-mode rows, converting to units itself.
        // Previously this defaulted straight to it.quantity (always stored in total
        // units), so an untouched/default receive submitted units where the backend
        // expected packs, inflating received quantity (and cost) by units_per_pack.
        received_quantity: it.units_per_pack
          ? ((it.received_quantity || it.quantity || 0) / it.units_per_pack)
          : (it.received_quantity || it.quantity || 0),
        free_quantity: (it.free_quantity && it.units_per_pack) ? it.free_quantity / it.units_per_pack : (it.free_quantity || 0),
        batch_number: it.batch_number || '',
        expiry_date: ymdToDate(it.expiry_date),
        is_expiring: it.is_expiring || it.product_is_expiring || false,
      })));
      setSelectedInvoiceId((currentOrder.supplier_invoices && currentOrder.supplier_invoices.length) ? currentOrder.supplier_invoices[0].id : null);
      const pref = selectedSupplier
        || currentOrder.supplier
        || (currentOrder.supplier_invoices && currentOrder.supplier_invoices.length && currentOrder.supplier_invoices[0].supplier)
        || (currentOrder.items[0] && currentOrder.items[0].supplier)
        || null;
      setReceiveSupplierId(pref);
    } else {
      setReceiveItemsState([]);
      setSelectedInvoiceId(null);
    }
  }, [currentOrder]);

  useEffect(() => {
    if (selectedProduct && !selectedProduct.is_expiring) {
      setExpiryDate(null);
    }
  }, [selectedProduct]);

  // Single source of truth for turning a server-side order's items into the local
  // editable item shape (deriving pack/unit mode per line). Every code path that loads
  // an order's items into local state should go through this, rather than each maintaining
  // its own copy — that duplication is exactly what let receiveItemsState and the item
  // mapping drift out of sync in different places.
  const mapOrderItemsToLocal = (orderItems) => (orderItems || []).map(it => {
    const rowMode = it.units_per_pack ? 'pack' : 'unit';
    return {
      product: it.product,
      product_name: it.product_name,
      quantity: it.quantity,
      // free_quantity is always kept in real units in local state, matching `quantity` and
      // matching computeFromPack/addItem/updateItem's convention — the summary table and
      // single-item form each divide by units_per_pack themselves when displaying packs.
      // Pre-dividing here caused a double-conversion (e.g. 30 units shown as 0.033 packs).
      free_quantity: it.free_quantity || 0,
      purchase_price: it.purchase_price,
      selling_price: it.selling_price,
      purchase_discount: it.purchase_discount ?? 0,
      expiry_date: it.expiry_date,
      batch_number: it.batch_number,
      reorder_level: it.reorder_level,
      supplier: it.supplier,
      price_list: it.price_list,
      line_total_cost: it.line_total_cost,
      unit_price_ui: it.unit_price_ui ?? null,
      pricing_mode: rowMode,
      pack_quantity: rowMode === 'pack' ? (it.pack_quantity ?? it.quantity ?? null) : null,
      units_per_pack: rowMode === 'pack' ? it.units_per_pack : null,
      pack_price: rowMode === 'pack' ? it.pack_price : null,
      rol_pack: (rowMode === 'pack' && it.units_per_pack && it.reorder_level) ? it.reorder_level / it.units_per_pack : null,
      free_pack_quantity: (rowMode === 'pack' && it.units_per_pack && it.free_quantity) ? it.free_quantity / it.units_per_pack : 0,
      unit_qty: rowMode === 'unit' ? it.quantity : null,
      unit_price_input: rowMode === 'unit' ? (it.unit_price_ui ?? it.purchase_price ?? null) : null,
      unit_free_qty: rowMode === 'unit' ? (it.free_quantity || 0) : 0,
      unit_rol: rowMode === 'unit' ? (it.reorder_level ?? null) : null,
    };
  });

  const applyReinitOrder = (order) => {
    if (!order) return;

    // Clear any previous state as if starting fresh
    resetForm(true);           // your existing helper
    setCurrentOrder(null);     // ensure dialog is in "new order" mode

    // Map items from the cancelled order into purchase dialog items
    const mappedItems = mapOrderItemsToLocal(order.items);
    setItems(mappedItems);

    // Set header fields like supplier from first item, if present
    if (order.items && order.items.length > 0) {
      const first = order.items[0];
      const supplierId = first.supplier || null;
      setSelectedSupplier(supplierId);

      const found = (suppliers || []).find(s => s.id === supplierId);
      if (found) {
        setSupplierCredit(
          found.credit_limit != null ? Number(found.credit_limit) : null
        );
        setSupplierCreditDays(found.credit_duration || null);
        setSupplierEmail(found.email);
      } else {
        setSupplierCredit(null);
        setSupplierCreditDays(null);
      }
    } else {
      setSelectedSupplier(null);
      setSupplierCredit(null);
      setSupplierCreditDays(null);
    }

    // Use templateFillMode/meta like a "from order" template
    setTemplateFillMode(true);
    setTemplateMeta({
      source: 'reinitiate',
      order_number: order.order_number,
    });
  };

  // Continue working on an existing draft order later — unlike applyReinitOrder (which
  // deliberately clears currentOrder to start a fresh copy of a cancelled order), this
  // keeps currentOrder pointed at the real order so Save/Send/Receive act on it directly.
  const loadDraftForEditing = async (orderId) => {
    try {
      const { data: order } = await axiosInstance.get(`/api/inventory/move-order/${orderId}/`);
      if (order.status !== 'draft') {
        handleApiError({ Error: 'Only draft orders can be reopened for editing.' });
        return;
      }

      resetForm(true);
      setCurrentOrder(order);
      setIsDirectGrn(!!order.is_direct_grn);

      const mappedItems = mapOrderItemsToLocal(order.items);
      setItems(mappedItems);

      if (order.items && order.items.length > 0) {
        const first = order.items[0];
        const supplierId = first.supplier || null;
        setSelectedSupplier(supplierId);
        const found = (suppliers || []).find(s => s.id === supplierId);
        if (found) {
          setSupplierCredit(found.credit_limit != null ? Number(found.credit_limit) : null);
          setSupplierCreditDays(found.credit_duration || null);
          setSupplierEmail(found.email);
        } else {
          setSupplierCredit(null);
          setSupplierCreditDays(null);
        }
      } else {
        setSelectedSupplier(null);
        setSupplierCredit(null);
        setSupplierCreditDays(null);
      }
    } catch (e) {
      handleApiError(e);
    }
  };

  const minExpiryDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + blockExpiryDays);
    return d;
  }, [blockExpiryDays]);

  const focusAny = (ref) => {
    setTimeout(() => {
      const el = ref?.current;
      if (!el) return;
      if (typeof el.focus === 'function') el.focus();
      else if (typeof el.getElement === 'function') el.getElement().focus();
    }, 0);
  };

  const blurOnEnter = (e, nextRef) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.currentTarget?.blur?.();
    if (nextRef) focusAny(nextRef);
  };

  const handleCancel = () => {
    try {
      // clear current order and UI state associated with a created PO
      setCurrentOrder(null);
      setItems([]);
      resetForm(true);
      setTemplateFillMode(false);
      setTemplateMeta(null);
      setSelectedRowsQuick([]);
      setIsDirectGrn(true);
      setSupplierCredit(null);
      setSupplierCreditDays(null);
      // also hide any open sub-dialogs related to the order
      setSendDialogVisible(false);
      setReceiveDialogVisible(false);
      setPaymentDialogVisible(false);
    } finally {
      // call parent hide (keep existing behavior)
      if (typeof onHide === 'function') onHide();
    }
  };

  const cancelCurrentOrder = async () => {
    if (!currentOrder?.id) {
      handleApiError({ Error: 'No created order to cancel' });
      return;
    }
    if (!window.confirm('Are you sure you want to cancel this move order?')) return;

    try {
      setCancelLoading(true);
      const res = await axiosInstance.post(`/api/inventory/move-order/${currentOrder.id}/cancel/`);
      handleApiSuccess(res.data?.message || 'Move order cancelled');
      onPurchased?.();
      await refreshCurrentOrder(currentOrder.id);
    } catch (e) {
      handleApiError(e);
    } finally {
      setCancelLoading(false);
    }
  };


  // Create GRN dialog submit handler
  const handleCreateGrnSubmit = async () => {
    try {
      // client-side validation
      if (grnReceiveOnCredit) {
        if (!grnSelectedSupplier) {
          handleApiError({ Error: 'Supplier is required when receiving on credit' });
          return;
        }
        if (!grnSupplierInvoiceNo || grnSupplierInvoiceNo.trim().length === 0) {
          handleApiError({ Error: 'Supplier invoice number is required when receiving on credit' });
          return;
        }
      }

      setGrnSubmitting(true);

      // build create payload (same as handlePurchase but include GRN receive fields)
      const createPayload = {
        is_purchase: true,
        is_direct_grn: true,
        source_department: null,
        destination_department: userDept ?? null,
        receive_on_credit: !!grnReceiveOnCredit,
        supplier: grnReceiveOnCredit ? grnSelectedSupplier : null,
        supplier_invoice_number: grnReceiveOnCredit ? (grnSupplierInvoiceNo || null) : null,
        supplier_credit_days: supplierCreditDays ?? null, 
        items: buildItemsPayload(),
      };

      const res = await axiosInstance.post('/api/inventory/move-order/', createPayload);
      handleApiSuccess(res.data?.message || 'GRN created');
      onPurchased?.();

      // set created order as current (backend should return created object)
      if (res.data && res.data.id) {
        // use existing refreshCurrentOrder if available, else setCurrentOrder
        if (typeof refreshCurrentOrder === 'function') {
          await refreshCurrentOrder(res.data.id);
        } else {
          setCurrentOrder(res.data);
        }
      }

      setGrnDialogVisible(false);
    } catch (e) {
      handleApiError(e);
    } finally {
      setGrnSubmitting(false);
    }
  };

const allowedOrderStatuses = new Set([
    'received',           
    'partially_received', 
    'partially_paid',
  ]);

  const allowedPaymentStatuses = new Set([
    'payment_pending', 
    'partially_paid',
    'partial',         
    'recieved',            
  ]);

  // compute whether Record Payment should be enabled
  const paymentEnabled = !!currentOrder && (
    allowedOrderStatuses.has(currentOrder?.status) ||
    allowedPaymentStatuses.has(currentOrder?.payment_status) ||
    (currentOrder?.receive_on_credit && ['received', 'partially_received', 'payment_pending'].includes(currentOrder?.status))
  );

  // Fetch price list when product is selected (single add mode)
  const fetchProductPrices = async (prodId) => {
    try {
      const res = await axiosInstance.get(`/api/products/${prodId}/active-pricelist/`);
      const flag = res.data.pricing_type || 'pack';
      setProductPricingType(flag);
      // "Unit Stocks" forces every product into Unit mode regardless of its own flag.
      // Otherwise, default to whatever this specific product is persistently flagged as —
      // not whether pack-price data happens to exist, and not whatever mode the previous
      // product in this session used.
      const effectiveMode = unitStocksMode ? 'unit' : flag;
      setPricingMode(effectiveMode);
      if (effectiveMode === 'pack') {
        setUnitsPerPack(res.data.units_per_pack || null);
        setPackPrice(res.data.pack_price || null);
        setRl(res.data.reorder_level && res.data.units_per_pack ? res.data.reorder_level / res.data.units_per_pack  : null);
        setUnitPriceUI(null);
      } else {
        setUnitsPerPack(null);
        setPackPrice(null);
        setRl(res.data.reorder_level ?? null);
        setUnitPriceUI(res.data.purchase_price ?? null);
      }
      setSellingPrice(res.data.selling_price || 0);
    } catch {
      setUnitPriceUI(null);
      setSellingPrice(null);
      setUnitsPerPack(null);
    }
  };

    const poTotal = useMemo(() => {
      return items.reduce((s, it) => {
        const val = Number(it.line_total_cost ?? 0);
        return s + (isFinite(val) ? val : 0);
      }, 0);
  }, [items]);

  // Open supplier dropdown and focus the internal filter box
  const openSupplierAndFocusFilter = () => {
    const dd = supplierRef.current;
    dd?.focus?.();
    dd?.show?.();
    if (!dd?.show) dd?.getElement?.()?.click?.();

    setTimeout(() => {
      const filterInput = document.querySelector('.p-dropdown-panel .p-dropdown-filter');
      filterInput?.focus();
      filterInput?.select?.();
    }, 30);
  };

  // --- Batch number helpers ---
  const generateBatchNumber = (product, supplier, expiry, pPrice, sPrice) => {
    if (!product || pPrice == null || sPrice == null) return '';
    const productCode = (product.code || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    const purchasePart = String(Math.round(Number(pPrice) * 100));
    const sellingPart = String(Math.round(Number(sPrice) * 100));
    const suppliercode = supplier || '';
    const parts = [productCode, suppliercode];
    if (product.is_expiring && expiry) {
      const exp = toYmd(expiry).replace(/-/g, '');
      parts.push(exp);
    }
    parts.push(purchasePart, sellingPart);
    return parts.filter(Boolean).join('-');
  };

  const generateRowBatch = (row) => {
    const productCode = (row.code || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    const pPart = row.purchase_price != null ? String(Math.round(Number(row.purchase_price) * 100)) : null;
    const sPart = row.selling_price != null ? String(Math.round(Number(row.selling_price) * 100)) : null;
    const parts = [productCode, row.supplier || ''];
    if (row.is_expiring && row.expiry_date) parts.push(String(row.expiry_date).replaceAll('-', ''));
    if (pPart != null && sPart != null) parts.push(pPart, sPart);
    return parts.filter(Boolean).join('-');
  };

  // Auto-generate batch number for single add mode; preserve on edit
  useEffect(() => {
    if (selectedProduct && (!selectedProduct.is_expiring || expiryDate) && purchasePrice !== null && sellingPrice !== null) {
      if (editingIndex !== null && batchNumber) {
        const existingCode = batchNumber.split('-')[0];
        const newBatch = generateBatchNumber(selectedProduct, selectedSupplier, expiryDate, purchasePrice, sellingPrice);
        const newParts = newBatch.split('-');
        newParts[0] = existingCode;
        setBatchNumber(newParts.join('-'));
      } else {
        if (editingIndex === null) setBatchNumber(generateBatchNumber(selectedProduct, selectedSupplier, expiryDate, purchasePrice, sellingPrice));
      }
    }
  }, [selectedProduct, expiryDate, purchasePrice, sellingPrice, selectedSupplier, editingIndex]);

  // Barcode scanner logic (single add mode)
  useEffect(() => {
    let controls = null;
    if (scannerVisible) {
      const reader = new BrowserMultiFormatReader();
      reader
        .decodeFromVideoDevice(
          null,
          'move-order-barcode-video',
          (result, error, ctrl) => {
            if (result) {
              onScan(result.getText());
              ctrl.stop();
              controls = null;
              setScannerVisible(false);
            }
            if (!controls) controls = ctrl;
          }
        )
        .catch((err) => {
          console.error('Camera start error:', err);
        });
    }
    return () => {
      if (controls) controls.stop();
    };
  }, [scannerVisible]);

  const onScan = async (code) => {
    setScannerVisible(false);
    try {
      const res = await axiosInstance.get(`/api/products?barcode=${code}/`);
      if (res.data?.id) {
        const prod = res.data;
        prod.displayLabel = prod.name;
        setSelectedProduct(prod);
        setProductQuery(prod.displayLabel);
        fetchProductPrices(prod.id);
        handleApiSuccess(`Found: ${res.data.name}`);
        setTimeout(() => qtyRef.current?.focus?.(), 0);
      } else {
        handleApiError({ Error: 'Product not found' });
      }
    } catch (err) {
      handleApiError(err);
    }
  };

  // Validation for single add/update
  const validate = () => {
    if (!selectedProduct) { handleApiError({ Error: 'Select a product' }); productRef.current?.focus?.(); return false; }
    if (!qty) { handleApiError({ Error: 'Enter a quantity' }); qtyRef.current?.focus?.(); return false; }
    if (!purchasePrice || !sellingPrice) { handleApiError({ Error: 'Enter both prices' }); return false; }
    if (isDirectGrn && selectedProduct.is_expiring && !expiryDate) { handleApiError({ Error: 'Enter expiry date' }); expiryRef.current?.focus?.(); return false; }
    return true;
  };

  const statusBody = (status) => {
      const severity =
        status === 'Completed' ? 'success' :
        status === 'Cancelled' ? 'danger' : 'info';
      return <Tag value={status} severity={severity} />;
    };
  
  // Switch between Pack and Unit entry modes; clears whichever fields don't apply
  // to the new mode so stale values can't leak into the totals or the payload.
  const switchPricingMode = (mode) => {
    if (unitStocksMode) return; // locked while Unit Stocks override is active
    setPricingMode(mode);
    if (mode === 'unit') {
      setUnitsPerPack(null);
      setPackPrice(null);
    }
    setUnitPriceUI(null);
    setLineTotalCostComputed(null);
    setPurchasePrice(null);
  };

  const computeFromPack = (packQty, unitsPerPackVal, packPriceVal, freePackQty, discountPct = 0) => {
    if (!packQty || !unitsPerPackVal || !packPriceVal) return {};

    const qtyUnits = packQty * unitsPerPackVal;
    const freeUnits = (freePackQty || 0) * unitsPerPackVal;
    const totalUnits = qtyUnits + freeUnits;

    const grossLineTotal = packQty * packPriceVal;
    const lineTotal = grossLineTotal * (1 - (Number(discountPct) || 0) / 100);
    const purchase = totalUnits > 0 ? lineTotal / totalUnits : null;

    return {
      quantity: qtyUnits,
      free_quantity: freeUnits,
      line_total_cost: lineTotal,
      purchase_price: purchase,
    };
  };

  const computeLineTotal = (unitPrice, qtyVal, discountPct = 0) => {
    try {
      if (unitPrice == null || qtyVal == null) return null;
      const gross = Number(unitPrice) * Number(qtyVal);
      return gross * (1 - (Number(discountPct) || 0) / 100);
    } catch (e) { return null; }
  };

  const computePurchasePrice = (lineTotal, qtyVal, freeQtyVal) => {
    try {
      if (lineTotal == null || qtyVal == null) return null;
      const totalUnits = Number(qtyVal) + Number(freeQtyVal || 0);
      if (totalUnits <= 0) return null;
      return Number(lineTotal) / totalUnits;
    } catch (e) { return null; }
  };

  // Add product to items (single add mode)
  // If the user deliberately switched this product's mode away from its persisted default,
  // save that back to the product record so future GRNs/POs default to it correctly.
  // Skipped while "Unit Stocks" is forcing the mode — that's a one-off PO-wide override,
  // not a statement about this product's normal pricing going forward.
  const syncProductPricingTypeIfChanged = async () => {
    if (unitStocksMode) return;
    if (!selectedProduct?.id) return;
    if (pricingMode === productPricingType) return;
    try {
      await axiosInstance.patch(`/api/products/${selectedProduct.id}/pricing-type/`, { pricing_type: pricingMode });
      setProductPricingType(pricingMode);
    } catch (e) {
      handleApiError(e);
    }
  };

  const addItem = () => {
    if (!validate()) return;
    if (items.some(i => i.product === selectedProduct.id && i.batch_number === batchNumber)) {
      handleApiError({ Error: 'Batch already added' });
      productRef.current?.focus?.();
      return;
    }
    let computed = {};

    if (pricingMode === 'pack') {
      computed = computeFromPack(qty, unitsPerPack, packPrice, freeQty, purchaseDiscount);
    } else {
      const computedLineTotal = computeLineTotal(unitPriceUI, qty, purchaseDiscount);
      const computedPurchase = computePurchasePrice(computedLineTotal, qty, freeQty);

      computed = {
        quantity: qty,
        free_quantity: freeQty,
        line_total_cost: computedLineTotal,
        purchase_price: computedPurchase,
      };
    }
    setItems(prev => ([...prev, {
      product: selectedProduct.id,
      product_name: selectedProduct.name,
      code: selectedProduct.code || '',
      is_expiring: !!selectedProduct.is_expiring,
      quantity: computed.quantity,
      reorder_level: (pricingMode === 'pack' && unitsPerPack && rl) ? rl * unitsPerPack : rl,
      purchase_price: computed.purchase_price ?? purchasePrice,
      line_total_cost: computed.line_total_cost,
      pack_quantity: pricingMode === 'pack' ? qty : null,
      units_per_pack: pricingMode === 'pack' ? unitsPerPack : null,
      pack_price: pricingMode === 'pack' ? packPrice : null,
      unit_price_ui: pricingMode === 'pack' ? ((unitsPerPack && packPrice) ? (packPrice / unitsPerPack) : null) : (unitPriceUI ?? null),
      free_quantity: computed.free_quantity,
      selling_price: sellingPrice,
      purchase_discount: purchaseDiscount ?? 0,
      expiry_date: selectedProduct.is_expiring ? (expiryDate ? toYmd(expiryDate) : null) : null,
      batch_number: batchNumber,
      supplier: selectedSupplier,
    }]));
    syncProductPricingTypeIfChanged();
    resetForm();
    setTimeout(() => productRef.current?.focus?.(), 0);
  };

  // Update existing item (single add mode)
  const updateItem = () => {
    if (!validate()) return;
    if (editingIndex == null) return;
    if (items.some((i, idx) => idx !== editingIndex && i.product === selectedProduct.id && i.batch_number === batchNumber)) {
      handleApiError({ Error: 'Another row already has this product + batch' });
      return;
    }
    let computed = {};

    if (pricingMode === 'pack') {
      computed = computeFromPack(qty, unitsPerPack, packPrice, freeQty, purchaseDiscount);
    } else {
      const computedLineTotal = computeLineTotal(unitPriceUI, qty, purchaseDiscount);
      const computedPurchase = computePurchasePrice(computedLineTotal, qty, freeQty);

      computed = {
        quantity: qty,
        free_quantity: freeQty,
        line_total_cost: computedLineTotal,
        purchase_price: computedPurchase,
      };
    }
    const updated = [...items];
    updated[editingIndex] = {
      product: selectedProduct.id,
      product_name: selectedProduct.name,
      code: selectedProduct.code || '',
      is_expiring: !!selectedProduct.is_expiring,
      quantity: computed.quantity,
      reorder_level: (pricingMode === 'pack' && unitsPerPack && rl) ? rl * unitsPerPack : rl,
      purchase_price: computed.purchase_price ?? purchasePrice,
      line_total_cost: computed.line_total_cost,
      unit_price_ui: pricingMode === 'pack' ? ((unitsPerPack && packPrice) ? (packPrice / unitsPerPack) : null) : (unitPriceUI ?? null),
      free_quantity: computed.free_quantity,
      pack_quantity: pricingMode === 'pack' ? qty : null,
      units_per_pack: pricingMode === 'pack' ? unitsPerPack : null,
      pack_price: pricingMode === 'pack' ? packPrice : null,
      selling_price: sellingPrice,
      purchase_discount: purchaseDiscount ?? 0,
      expiry_date: selectedProduct.is_expiring ? (expiryDate ? toYmd(expiryDate) : null) : null,
      batch_number: batchNumber,
      supplier: selectedSupplier,
    };
    setItems(updated);
    syncProductPricingTypeIfChanged();
    resetForm();
    setSelectedRow(null);
    setEditingIndex(null);
    setTimeout(() => productRef.current?.focus?.(), 0);
  };

  // Clear input fields; if full=true also clear selection/editing state
  const resetForm = (full=false) => {
    setSelectedProduct(null);
    setProductQuery('');
    setQty(null);
    setUnitsPerPack(null);
    setPackPrice(null);
    setRl(null);
    setPurchasePrice(null);
    setLineTotalCostComputed(null);
    setFreeQty(0);
    setPurchaseDiscount(0);
    setUnitPriceUI(null);
    setSellingPrice(null);
    setExpiryDate(null);
    setBatchNumber('');
    // No product selected yet after a reset — fall back to Unit Stocks override if active,
    // otherwise plain Pack, until the next product's own flag is fetched.
    setPricingMode(unitStocksMode ? 'unit' : 'pack');
    setProductPricingType('pack');
    if (full) {
      setSelectedSupplier(null);
      setSelectedRow(null);
      setEditingIndex(null);
      setSupplierCredit(null);
      setSupplierCreditDays(null);
    }
  };

  // Remove item
  const removeItem = (productId, batchNum) => {
    setItems(prev => prev.filter(i => !(i.product === productId && i.batch_number === batchNum)));
    if (selectedRow && selectedRow.product === productId && selectedRow.batch_number === batchNum) {
      resetForm(); setSelectedRow(null); setEditingIndex(null);
    }
  };

  // --- Purchase ---
  // Rounds to 2dp before sending — computeFromPack/computeLineTotal/computePurchasePrice
  // produce raw floating-point division results (e.g. 128.29285714285714), which have far
  // more significant digits than the backend's DecimalField(max_digits=10, decimal_places=2)
  // allows, and get rejected with "Ensure that there are no more than 10 digits in total."
  const round2 = (v) => (v == null || v === '' || isNaN(Number(v))) ? v : Math.round(Number(v) * 100) / 100;

  // Shared item payload shape used by both creating a new order and saving edits to a draft
  const buildItemsPayload = () => items.map(i => ({
    product: i.product,
    is_direct_grn: isDirectGrn,
    quantity: i.quantity,
    purchase_price: round2(i.purchase_price),
    selling_price: round2(i.selling_price),
    purchase_discount: i.purchase_discount ?? 0,
    expiry_date: i.expiry_date,
    batch_number: i.batch_number,
    reorder_level: i.reorder_level,
    supplier: i.supplier,
    price_list: i.price_list,
    line_total_cost: round2(i.line_total_cost),
    free_quantity: i.units_per_pack ? i.free_quantity / i.units_per_pack : (i.free_quantity || 0),
    unit_price_ui: round2(i.unit_price_ui),
    pack_quantity: i.units_per_pack ? i.pack_quantity : null,
    units_per_pack: i.units_per_pack,
    pack_price: round2(i.pack_price),
  }));

  const handlePurchase = async () => {
    if (savingOrderRef.current) return; // already in flight — ignore a second click, synchronously
    if (items.length === 0) { handleApiError({ severity: 'error', summary: 'No items', detail: 'Add at least one item', life: 3000 }); return; }
    // Validate incomplete rows if in template fill mode
    if (templateFillMode) {
      const incomplete = items.filter(r => (!r.quantity) || (isDirectGrn && r.is_expiring && !r.expiry_date) || r.purchase_price == null || r.selling_price == null);
      if (incomplete.length) { handleApiError({ Error: `Please complete ${incomplete.length} row(s) before purchasing.` }); return; }
    }
    savingOrderRef.current = true;
    setSavingOrder(true);
    try {
      const payload = {
        is_purchase: true,
        source_department: null,
        destination_department: userDept ?? null,
        supplier_credit_days: supplierCreditDays ?? null,
        items: buildItemsPayload(),
      };
      const res = await axiosInstance.post('/api/inventory/move-order/', payload);
      const orderId = res.data.id;
      handleApiSuccess('Purchase Order created (draft).');
      onPurchased?.();
      try {
        await refreshCurrentOrder(orderId);
      } catch (fetchErr) {
        setCurrentOrder(null);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      savingOrderRef.current = false;
      setSavingOrder(false);
    }
  };

  // Save edits (added/removed/changed items, header fields) back to an existing draft
  // order, so the user can pick up where they left off later instead of re-entering
  // everything or being locked out once the order has been created.
  const handleSaveDraft = async () => {
    if (savingOrderRef.current) return false; // already in flight — ignore a second click, synchronously
    if (!currentOrder?.id) { handleApiError({ Error: 'No order to save' }); return false; }
    if (currentOrder.status !== 'draft') { handleApiError({ Error: 'Only draft orders can be saved' }); return false; }
    if (items.length === 0) { handleApiError({ severity: 'error', summary: 'No items', detail: 'Add at least one item', life: 3000 }); return false; }
    if (templateFillMode) {
      const incomplete = items.filter(r => (!r.quantity) || (isDirectGrn && r.is_expiring && !r.expiry_date) || r.purchase_price == null || r.selling_price == null);
      if (incomplete.length) { handleApiError({ Error: `Please complete ${incomplete.length} row(s) before saving.` }); return false; }
    }
    savingOrderRef.current = true;
    setSavingOrder(true);
    try {
      const payload = {
        destination_department: userDept ?? null,
        is_direct_grn: isDirectGrn,
        items: buildItemsPayload(),
      };
      await axiosInstance.post(`/api/inventory/move-order/${currentOrder.id}/save-draft/`, payload);
      handleApiSuccess('Draft saved — you can continue this order later.');
      onPurchased?.();
      await refreshCurrentOrder(currentOrder.id);
      return true;
    } catch (err) {
      handleApiError(err);
      return false;
    } finally {
      savingOrderRef.current = false;
      setSavingOrder(false);
    }
  };

  // --- API actions used by UI buttons ---
  const refreshCurrentOrder = async (orderId) => {
    try {
      const od = await axiosInstance.get(`/api/inventory/move-order/${orderId}/`);
      setCurrentOrder(od.data);
      const o = od.data || {};
      // Replace local items wholesale with the server's authoritative copy, rather than
      // merging into whatever's currently in local state. Merging-by-product silently broke
      // down whenever two rows shared a product with different batches, or local state had
      // drifted from the server (e.g. a retried Save) — leaving stale/duplicate-looking rows
      // that then got faithfully persisted as-is on the next Save.
      setItems(mapOrderItemsToLocal(o.items));
      return od.data;
    } catch (e) {
      handleApiError(e);
      return null;
    }
  };

  const sendForApproval = async () => {
    if (!currentOrder?.id) { handleApiError({ Error: 'No created order to send for approval' }); return; }
    try {
      const res = await axiosInstance.post(`/api/inventory/move-order/${currentOrder.id}/send_for_approval/`);
      handleApiSuccess(res.data?.message || 'Sent for approval');
      onPurchased?.();
      await refreshCurrentOrder(currentOrder.id);
    } catch (e) { handleApiError(e); }
  };

  const sendToSupplier = async (emailTo) => {
    if (!currentOrder?.id) { handleApiError({ Error: 'No created order to send to supplier' }); return; }
    try {
      const payload = {};
      if (emailTo) payload.email_to = emailTo;
      const res = await axiosInstance.post(`/api/inventory/move-order/${currentOrder.id}/send_to_supplier/`, payload);
      handleApiSuccess(res.data?.message || 'PO sent to supplier');
      onPurchased?.();
      await refreshCurrentOrder(currentOrder.id);
    } catch (e) { handleApiError(e); }
  };

  const receiveGoods = async ({ receive_on_credit = false, supplier_invoice_number = null, itemsPayload = null } = {}) => {
    if (!currentOrder?.id) { handleApiError({ Error: 'No created order to receive' }); return; }
    try {
      const order = currentOrder;
      let itemsToSend = itemsPayload;
      if (!itemsToSend) {
        if (!Array.isArray(order.items) || order.items.length === 0) { handleApiError({ Error: 'Order has no items to receive' }); return; }
        itemsToSend = order.items.map(it => ({ move_order_item: it.id, received_quantity: it.pack_quantity || 0, batch_number: it.batch_number || '', expiry_date: it.expiry_date || null }));
      }
      if (receive_on_credit && (!receiveSupplierId)) { handleApiError({ Error: 'Supplier is required when receiving on credit' }); return; }
      if (receive_on_credit && (!supplier_invoice_number || supplier_invoice_number.trim().length === 0)) { handleApiError({ Error: 'Supplier invoice number is required when receiving on credit' }); return; }
      const normalizedItems = (itemsToSend || []).map(it => {
        let expiry = it.expiry_date ?? null;
        if (expiry) {
          if (expiry instanceof Date) expiry = toYmd(expiry);
          else if (typeof expiry === 'string' && expiry.includes('T')) expiry = expiry.split('T')[0];
        }
        return { ...it, expiry_date: expiry };
      });
      const payload = { items: normalizedItems, receive_on_credit: receive_on_credit, supplier_invoice_number: supplier_invoice_number || null, supplier: receive_on_credit ? receiveSupplierId : null, supplier_credit_days: supplierCreditDays ?? null};
      const res = await axiosInstance.post(`/api/inventory/move-order/${order.id}/receive/`, payload);
      handleApiSuccess(res.data?.message || 'Goods received');
      onPurchased?.();
      await refreshCurrentOrder(order.id);
    } catch (e) { handleApiError(e); }
  };

  // Selecting a row in the classic table should populate the form for editing
  const onSelectionChange = (e) => {
    const row = e.value;
    setSelectedRow(row);
    if (!row) { resetForm(); setEditingIndex(null); return; }
    const idx = items.findIndex(i => i.product === row.product && i.batch_number === row.batch_number);
    setEditingIndex(idx >= 0 ? idx : null);
    const prodObj = { id: row.product, name: row.product_name, is_expiring: !!row.is_expiring || !!row.expiry_date, code: row.code || '' };
    setSelectedProduct(prodObj);
    setProductQuery(row.product_name);
    // Use ONLY this row's own units_per_pack -- falling back to the `unitsPerPack`
    // state variable here was the bug: that state holds whatever the PREVIOUSLY
    // selected row's units_per_pack was, so clicking a pack item then a unit item
    // would incorrectly divide the unit item's quantity/free_quantity by the
    // previous pack item's units_per_pack instead of treating it as 1:1.
    const unitsPerPackVal = row.units_per_pack || 1;
    const qtyVal = row.quantity ?? null;
    const freeVal = row.free_quantity ?? 0;

    // ✅ convert units → packs
    const packQty = qtyVal && unitsPerPackVal ? qtyVal / unitsPerPackVal : qtyVal;
    const freePackQty = freeVal && unitsPerPackVal ? freeVal / unitsPerPackVal : freeVal;
    const unitUi = row.unit_price_ui ?? (qtyVal && row.line_total_cost ? (Number(row.line_total_cost) / Number(qtyVal)) : row.purchase_price ?? null);
    const lineTotal = row.line_total_cost ?? (unitUi != null && qtyVal ? Number(unitUi) * Number(qtyVal) : null);
    const computedPurchase = computePurchasePrice(lineTotal, qtyVal, freeVal);
    const rolPack = row.reorder_level && unitsPerPackVal ? row.reorder_level / unitsPerPackVal : row.reorder_level;

    setQty(packQty);
    setRl(rolPack);
    setUnitPriceUI(unitUi);
    setLineTotalCostComputed(lineTotal);
    setFreeQty(freePackQty);
    setUnitsPerPack(row.units_per_pack || null);
    setPackPrice(row.pack_price || null);
    setPricingMode(row.units_per_pack ? 'pack' : 'unit');
    setPurchasePrice(computedPurchase ?? row.purchase_price ?? null);
    setSellingPrice(row.selling_price ?? null);
    setPurchaseDiscount(row.purchase_discount ?? 0);
    setExpiryDate(ymdToDate(row.expiry_date));
    setBatchNumber(row.batch_number || '');
    setSelectedSupplier(row.supplier ?? null);
    focusRef(qtyRef);
  };

  // --- Template actions ---
  const onSaveTemplate = async ({ name, supplierId }) => {
    try {
      if (!name || name.trim().length < 2) { handleApiError({ Error: 'Enter a template name' }); return; }
      const productIds = [...new Set(items.map(i => i.product))];
      if (productIds.length === 0) { handleApiError({ Error: 'No items to save in template' }); return; }
      await axiosInstance.post('/api/purchase-templates/', {
        name: name.trim(),
        supplier: supplierId || null,
        items: productIds.map(id => ({ product: id }))
      });
      handleApiSuccess('Template saved');
      setTemplateSaveVisible(false);
    } catch (e) { handleApiError(e); }
  };

  const onLoadTemplateChosen = async (tpl) => {
    try {
      setTemplateMeta({ id: tpl.id, name: tpl.name, supplier: tpl.supplier });
      const ids = tpl.items.map(i => i.product);
      const { data } = await axiosInstance.post('/api/products/active-pricelist/bulk/', { product_ids: ids });
      const map = new Map((data.products || []).map(p => [p.id, p]));
      const newRows = ids.map(pid => {
        const p = map.get(pid) || {};
        const rowMode = p.pricing_type || 'pack';
        return {
          product: pid,
          product_name: p.name || '(Unknown product)',
          code: p.code || '',
          is_expiring: !!p.is_expiring,
          pricing_mode: rowMode,
          pack_quantity: null,
          units_per_pack: rowMode === 'pack' ? (p.units_per_pack ?? null) : null,
          pack_price: rowMode === 'pack' ? (p.pack_price ?? null) : null,
          rol_pack: rowMode === 'pack' && p.reorder_level && p.units_per_pack? p.reorder_level / p.units_per_pack : null,
          unit_qty: null,
          unit_price_input: rowMode === 'unit' ? (p.purchase_price ?? null) : null,
          unit_free_qty: 0,
          unit_rol: rowMode === 'unit' ? (p.reorder_level ?? null) : null,
          quantity: null,
          reorder_level: p.reorder_level ?? null,
          unit_price_ui: null,
          line_total_cost: null,
          free_quantity: 0,
          free_pack_quantity: 0,
          purchase_price: p.purchase_price ?? null,
          selling_price: p.selling_price ?? null,
          purchase_discount: 0,
          expiry_date: null,
          batch_number: '', // will be computed as user fills
          supplier: tpl.supplier ?? selectedSupplier ?? null,
        };
      });
      setItems(newRows);
      if (tpl.supplier) setSelectedSupplier(tpl.supplier);
      const found = (suppliers || []).find(s => s.id === tpl.supplier);
      if (found) {
        setSupplierCredit(found.credit_limit != null ? Number(found.credit_limit) : null);
        setSupplierCreditDays(found.credit_duration != null? Number(found.credit_duration) : null);
        setSupplierEmail(found.email);
      } else {
        setSupplierCredit(null);
        setSupplierCreditDays(null);
      }
      setTemplateFillMode(true);
      setTemplateLoadVisible(false);
      handleApiSuccess(`Loaded template: ${tpl.name}`);
    } catch (e) { handleApiError(e); }
  };

  // --- Quick-Fill helpers ---
  // Per-cell refs for keyboard navigation in Quick-Fill grid
  const cellRefs = useRef({}); // key: `${rowIndex}:${field}` -> ref
  const getCellRef = (rowIndex, field) => {
    const key = `${rowIndex}:${field}`;
    if (!cellRefs.current[key]) cellRefs.current[key] = React.createRef();
    return cellRefs.current[key];
  };

  // Field order for template fill keyboard navigation (expiry skipped if not expiring),
  // kept separate per pricing mode since Pack and Unit rows render different fields.
  const ORDER_PACK_EXP   = ['pack_quantity','units_per_pack','pack_price','rol_pack','expiry_date','selling_price','purchase_discount','free_pack_quantity'];
  const ORDER_PACK_NOEXP = ['pack_quantity','units_per_pack','pack_price','rol_pack','selling_price','purchase_discount','free_pack_quantity'];
  const ORDER_UNIT_EXP   = ['unit_qty','unit_price_input','unit_rol','expiry_date','selling_price','purchase_discount','unit_free_qty'];
  const ORDER_UNIT_NOEXP = ['unit_qty','unit_price_input','unit_rol','selling_price','purchase_discount','unit_free_qty'];

  const focusField = (rowIndex, field) => {
    const ref = getCellRef(rowIndex, field);
    setTimeout(() => ref.current?.focus?.(), 0);
  };

  const onCellKeyDown = (e, rowIndex, field, rowData) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const isUnit = rowData?.pricing_mode === 'unit';
    const order = isUnit
      ? (rowData?.is_expiring ? ORDER_UNIT_EXP : ORDER_UNIT_NOEXP)
      : (rowData?.is_expiring ? ORDER_PACK_EXP : ORDER_PACK_NOEXP);
    const idx = order.indexOf(field);
    if (idx === -1) return;
    if (idx < order.length - 1) {
      focusField(rowIndex, order[idx + 1]);
    } else {
      // last field — advance to first field of next row, matching THAT row's own mode
      const nextRow = rowIndex + 1;
      if (nextRow < items.length) {
        const nextIsUnit = items[nextRow]?.pricing_mode === 'unit';
        focusField(nextRow, nextIsUnit ? 'unit_qty' : 'pack_quantity');
      } else {
        e.currentTarget?.blur?.();
      }
    }
  };

  // Derive the canonical submitted fields (quantity, free_quantity, line_total_cost,
  // purchase_price, unit_price_ui, reorder_level, pack_quantity/units_per_pack/pack_price)
  // from whichever scratch inputs are active for this row's pricing_mode. Pack and Unit
  // rows keep separate scratch fields so switching a row's mode never clobbers the other
  // mode's entered values.
  const recomputeRow = (r) => {
    if (r.pricing_mode === 'unit') {
      const q = r.unit_qty ?? 0;
      const price = r.unit_price_input ?? 0;
      const freeQ = r.unit_free_qty ?? 0;
      const lineTotal = computeLineTotal(price, q, r.purchase_discount ?? 0);
      const purchase = computePurchasePrice(lineTotal, q, freeQ);
      r.quantity = q || null;
      r.free_quantity = freeQ || 0;
      r.line_total_cost = lineTotal;
      r.purchase_price = purchase;
      r.unit_price_ui = price || null;
      r.reorder_level = r.unit_rol ?? null;
      r.pack_quantity = null;
      r.units_per_pack = null;
      r.pack_price = null;
    } else {
      const packQty = r.pack_quantity ?? 0;
      const upp = r.units_per_pack ?? 0;
      const packPrice = r.pack_price ?? 0;
      const freePack = r.free_pack_quantity ?? 0;

      if (packQty && upp && packPrice) {
        const computed = computeFromPack(packQty, upp, packPrice, freePack, r.purchase_discount ?? 0);
        r.quantity = computed.quantity;
        r.free_quantity = computed.free_quantity;
        r.line_total_cost = computed.line_total_cost;
        r.purchase_price = computed.purchase_price;
        r.unit_price_ui = packPrice / upp;
      } else {
        r.quantity = null;
        r.free_quantity = 0;
        r.line_total_cost = null;
        r.purchase_price = null;
        r.unit_price_ui = null;
      }
      r.reorder_level = (upp && r.rol_pack != null) ? r.rol_pack * upp : (r.rol_pack ?? null);
    }
    return r;
  };

  const updateItemField = (rowIndex, field, value) => {
    setItems(prev => {
      const next = [...prev];
      let r = { ...next[rowIndex], [field]: value };

      // expiry normalize
      if (field === 'expiry_date' && value instanceof Date) {
        r.expiry_date = toYmd(value);
      }

      r = recomputeRow(r);
      r.batch_number = generateRowBatch(r);
      next[rowIndex] = r;
      return next;
    });
  };

  const selectedIndexes = useMemo(() => new Set(selectedRowsQuick.map(r => items.findIndex(i => i.product === r.product && i.product_name === r.product_name))), [selectedRowsQuick, items]);

  const setExpiryForSelected = (date) => {
    if (!date) return;
    const ymd = toYmd(date);
    setItems(prev => prev.map((r, i) => (selectedIndexes.has(i) && r.is_expiring) ? { ...r, expiry_date: ymd, batch_number: generateRowBatch({ ...r, expiry_date: ymd }) } : r));
  };

  const reapplyPricesForSelected = async () => {
    try {
      const ids = [...new Set(selectedRowsQuick.map(r => r.product))];
      if (ids.length === 0) return;
      const { data } = await axiosInstance.post('/api/products/active-pricelist/bulk/', { product_ids: ids });
      const map = new Map((data.products || []).map(p => [p.id, p]));
      setItems(prev => prev.map((r, i) => {
        if (!selectedIndexes.has(i)) return r;
        const p = map.get(r.product) || {};
        let rr = { ...r, selling_price: p.selling_price ?? r.selling_price };
        if (rr.pricing_mode === 'unit') {
          rr.unit_price_input = p.purchase_price ?? rr.unit_price_input;
          rr.unit_rol = p.reorder_level ?? rr.unit_rol;
        } else {
          rr.units_per_pack = p.units_per_pack ?? rr.units_per_pack;
          rr.pack_price = p.pack_price ?? rr.pack_price;
          rr.rol_pack = (p.reorder_level && p.units_per_pack) ? p.reorder_level / p.units_per_pack : rr.rol_pack;
        }
        rr = recomputeRow(rr);
        rr.batch_number = generateRowBatch(rr);
        return rr;
      }));
    } catch (e) { handleApiError(e); }
  };

  const setQtyEqualROLForSelected = () => {
    setItems(prev => prev.map((r, i) => {
      if (!selectedIndexes.has(i)) return r;
      let rr = { ...r };
      if (rr.pricing_mode === 'unit') {
        rr.unit_qty = rr.unit_rol || 0;
      } else {
        rr.pack_quantity = rr.units_per_pack ? (rr.rol_pack || 0) : (rr.reorder_level || 0);
      }
      rr = recomputeRow(rr);
      rr.batch_number = generateRowBatch(rr);
      return rr;
    }));
  };

  const clearQtyForSelected = () => {
    setItems(prev => prev.map((r, i) => {
      if (!selectedIndexes.has(i)) return r;
      let rr = { ...r };
      if (rr.pricing_mode === 'unit') {
        rr.unit_qty = null;
      } else {
        rr.pack_quantity = null;
      }
      rr = recomputeRow(rr);
      rr.batch_number = generateRowBatch(rr);
      return rr;
    }));
  };

  const incompleteCount = useMemo(() => items.reduce((acc, r) => acc + ((isDirectGrn && r.is_expiring && !r.expiry_date) || !r.quantity || r.purchase_price == null || r.selling_price == null ? 1 : 0), 0), [items]);

  const templateMenuItems = [
    { label: 'Save as Template…', icon: 'pi pi-save', command: () => setTemplateSaveVisible(true) },
    { label: 'Load Template…', icon: 'pi pi-upload', command: () => setTemplateLoadVisible(true), disabled: unitStocksMode },
  ];

  return (
    <Dialog 
    header= {
            <div>
              <div>
                  <div> 
                    <span>
                        Purchase Order: {currentOrder?.order_number || 'New'}
                        {templateFillMode && templateMeta?.source === 'reinitiate' && (
                          <span className="ml-2 text-xs text-gray-500">
                            (Re-initiated from {templateMeta.order_number})
                          </span>
                        )}
                    </span>
                    <span style={{ marginLeft: 12 }}>
                  {/* Direct GRN checkbox */}
                    <Checkbox inputId="directGrn" checked={isDirectGrn} onChange={(e) => setIsDirectGrn(!!e.checked)} disabled={orgSettings && orgSettings.allow_direct_grn_auto_approve === false || isReadOnlyOrder} />
                    <label htmlFor="directGrn" style={{ margin: 0 }}>Direct GRN {orgSettings && orgSettings.allow_direct_grn_auto_approve === false ? <span className="text-sm font-medium"> (Disabled by organization Settings)</span> : ''}</label></span>
                    <span style={{ marginLeft: 12 }}>
                  {/* Unit Stocks checkbox — for entering existing shelf-counted stock with no pack concept */}
                    <Checkbox
                      inputId="unitStocks"
                      checked={unitStocksMode}
                      disabled={isReadOnlyOrder || templateFillMode}
                      onChange={(e) => {
                        const v = !!e.checked;
                        setUnitStocksMode(v);
                        if (v) {
                          setPricingMode('unit');
                        } else if (selectedProduct) {
                          setPricingMode(productPricingType || 'pack');
                        } else {
                          setPricingMode('pack');
                        }
                      }}
                    />
                    <label htmlFor="unitStocks" style={{ margin: 0 }}>Unit Stocks {templateFillMode ? <span className="text-sm font-medium"> (Not available with templates)</span> : ''}</label></span>
                  </div>
              <div className="flex flex-wrap align-items-center gap-2 sm:gap-2" style={{ margin: '10px' }}>
                {/* Create button: label changes when Direct GRN is selected */}
                <Button
                  label={isDirectGrn ? 'Create GRN' : 'Create PO'}
                  icon="pi pi-check"
                  onClick={() => {
                    if (isDirectGrn) {
                      setGrnSelectedSupplier(selectedSupplier || null);
                      setGrnReceiveOnCredit(true);
                      setGrnSupplierInvoiceNo('');
                      setGrnDialogVisible(true);
                    } else {
                      handlePurchase();
                    }
                  }}
                  disabled={items.length === 0 || !!currentOrder || savingOrder}
                  loading={savingOrder}
                  className="p-mr-2"
                  style={{ borderRadius: '6px', fontSize: '14px' }}
                  size="medium"
                />
                {/* Save: persist as a draft — works both for the initial save (before any
                    "Create GRN" confirmation) and for continuing edits on an existing draft */}
                {(!currentOrder || currentOrder?.status === 'draft') && (
                  <Button
                    label="Save"
                    icon="pi pi-save"
                    onClick={() => { if (currentOrder) { handleSaveDraft(); } else { handlePurchase(); } }}
                    disabled={items.length === 0 || savingOrder}
                    loading={savingOrder}
                    className="p-mr-2"
                    severity="secondary"
                    style={{ borderRadius: '6px', fontSize: '14px' }}
                    size="medium"
                  />
                )}
                {/* When Direct GRN is enabled, disable all other header actions */}
                {!isDirectGrn && !!currentOrder && currentOrder?.status === 'draft' && (
                  <Button
                    label="Send for Approval"
                    icon="pi pi-send"
                    onClick={sendForApproval}
                    className="p-mr-2"
                    severity="warning"
                    style={{ borderRadius: '6px', fontSize: '14px' }}
                    size="medium"
                  />
                )}
                {!isDirectGrn && !!currentOrder && ['approved','draft','pending_approval'].includes(currentOrder?.status) && !(orgSettings && orgSettings.require_approval_for_purchases && currentOrder?.status !== 'approved') && (
                  <Button
                    label="Send to Supplier"
                    icon="pi pi-envelope"
                    onClick={() => { setSendEmailTo(supplierEmail || ''); setSendDialogVisible(true); }}
                    className="p-mr-2"
                    severity="help"
                    style={{ borderRadius: '6px', fontSize: '14px' }}
                    size="medium"
                  />
                )}
                {!!currentOrder && ['approved','sent_to_supplier','partially_received','draft'].includes(currentOrder?.status) && !(currentOrder?.status === 'draft' && orgSettings && !orgSettings.allow_direct_grn_auto_approve && orgSettings.require_approval_for_purchases) && (
                  <Button
                    label="Receive Goods"
                    icon="pi pi-download"
                    loading={savingOrder}
                    onClick={async () => {
                      // If the order is still a draft, any item additions/removals made
                      // in this session live only in local `items` state -- currentOrder.items
                      // (and therefore the Receive Goods table, which is built from it) won't
                      // reflect them until saved. Auto-save first so nothing is silently
                      // dropped from the receive payload just because Save was forgotten.
                      if (currentOrder?.status === 'draft') {
                        const saved = await handleSaveDraft();
                        if (!saved) return; // save failed or was blocked -- don't open Receive with stale data
                      }
                      setReceiveOnCredit(true);
                      setReceiveInvoiceNo('');
                      setReceiveDialogVisible(true);
                    }}
                    className="p-mr-2"
                    severity="success"
                    style={{ borderRadius: '6px', fontSize: '14px' }}
                    size="medium"
                  />
                )}
                <Button
                  label="Record Payment"
                  icon="pi pi-money-bill"
                  onClick={() => { setPaymentDialogVisible(true); }}
                  disabled={!paymentEnabled}
                  style={{ borderRadius: '6px', fontSize: '14px' }}
                  size="medium"
                />
                <Button
                  label="Drop Order"
                  icon="pi pi-trash"
                  onClick={cancelCurrentOrder}
                  className="p-mr-2"
                  severity="danger"
                  style={{ borderRadius: '6px', fontSize: '14px' }}
                  size="medium"
                  outlined
                  disabled={!currentOrder || isCancelled || cancelLoading || currentOrder?.status === 'completed'}
                />
                              {/* Cancel should remain enabled even when Direct GRN is selected */}
                <Button
                  label="Print PO"
                  icon="pi pi-print"
                  onClick={() => {
                    const supplierObj = currentOrder
                      ? (suppliers || []).find(s => s.id === selectedSupplier) || null
                      : (suppliers || []).find(s => s.id === selectedSupplier) || null;
                    const orderData = currentOrder || {
                      order_number: '(Draft)',
                      items,
                      status_display: 'Draft',
                      destination_department_name: null,
                      created_by_name: JSON.parse(localStorage.getItem('userSettings') || '{}')?.username || '',
                      approved_by_name: null,
                      created_at: new Date().toISOString(),
                      approved_at: null,
                      total_purchase_price: items.reduce((s, i) => s + (Number(i.line_total_cost) || 0), 0),
                    };
                    printPurchaseOrder({ order: orderData, supplier: supplierObj });
                  }}
                  disabled={items.length === 0}
                  severity="info"
                  outlined
                  style={{ borderRadius: '6px', fontSize: '14px' }}
                  size="medium"
                />
                <Button label="Cancel" icon="pi pi-times" onClick={handleCancel} className="p-mr-2" severity="secondary" outlined style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" />
                </div>
            </div>
          </div>
    }
    visible={visible} onHide={onHide} style={{ width: '100%', height: '100%' }} modal draggable={false} closable={false} maximized={true} breakpoints={{ '960px': '75vw', '641px': '90vw' }} 
    >
      <div style={{ borderRadius: '12px', padding: '1rem' }}>
        <div className="formgrid grid">
          <div className="field col">
            <label className="font-bold">Supplier:</label>
            <Dropdown
              ref={supplierRef}
              value={selectedSupplier}
              options={suppliers}
              onChange={(e) => {
                const sid = e.value;
                setSelectedSupplier(sid);
                const found = (suppliers || []).find(s => s.id === sid);
                if (found) {
                  setSupplierCredit(found.credit_limit != null ? Number(found.credit_limit) : null);
                  setSupplierCreditDays(found.credit_duration != null? Number(found.credit_duration) : null);
                  setSupplierEmail(found.email);
                } else {
                  setSupplierCredit(null);
                  setSupplierCreditDays(null);
                  setSupplierEmail('');
                }
                if (!templateFillMode) focusAny(productRef);
              }}
              optionLabel="name" optionValue="id" placeholder="Select Supplier" filter
              onFocus={openSupplierAndFocusFilter}
              disabled={isReadOnlyOrder}
            />
          </div >
            <div className="field col">
              <label className="font-bold">Supplier Credit:</label>
              <div>
                {supplierCredit != null ? (
                  <span className="text-sm font-medium">LKR {Number(supplierCredit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                ) : (
                  <span className="text-sm text-muted">—</span>
                )}
              </div>
            </div>
            <div className="field col">
              <label className="font-bold">Credit Duration:</label>
              <div>
                {supplierCreditDays != null ? (
                  <span className="text-sm font-medium">
                    {supplierCreditDays} day{supplierCreditDays === 1 ? '' : 's'}
                  </span>
                ) : (
                  <span className="text-sm text-muted">—</span>
                )}
              </div>
            </div>
            <div className="field col">
              <label className="font-bold">Total:</label>
              <div>
                <span className="text-sm font-medium">
                  LKR {Number(currentOrder?.total_purchase_price ?? poTotal ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
            <div className="field col">
              <label className="font-bold">Paid:</label>
              <div>
                <span className="text-sm font-medium">
                  { currentOrder?.total_paid != null ? `LKR ${Number(currentOrder.total_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—' }
                </span>
              </div>
            </div>
            <div className="field col">
              <label className="font-bold">Balance:</label>
              <div>
                <span className="text-sm font-medium">
                  { currentOrder?.outstanding_balance != null ? `LKR ${Number(currentOrder.outstanding_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `LKR ${Math.max(0, Number(poTotal || 0) - Number(currentOrder?.total_paid || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }
                </span>
              </div>
            </div>
            <div className="field col">
              <label className="font-bold">Status:</label>
              <div>
                <span className="text-sm font-medium">
                  { currentOrder?.status_display ? currentOrder.status_display : (currentOrder?.status || 'Draft') }
                </span>
              </div>
            </div>
            <div className="field col">
              <SplitButton label="Template" icon="pi pi-clone" model={templateMenuItems} className="p-button-outlined" disabled={!!currentOrder}/>
            </div>
        </div>
        <div style={{marginTop: "5px", marginBottom: "20px", height: "1px", background: "linear-gradient(to right,rgb(200, 200, 200),rgb(200, 200, 200),rgb(200, 200, 200))", borderRadius: "2px", boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)"}}></div>
        {/* TEMPLATE FILL MODE */}
        {templateFillMode ? (
          <>
            <div className="p-2 mb-2" style={{ background: '#eef6ff', border: '1px solid #91caff', borderRadius: 6 }}>
              <b>Template:</b> {templateMeta?.name || 'Untitled'}
              {incompleteCount > 0 && (
                <span style={{ marginLeft: 12, color: '#fa8c16' }}>
                  • {incompleteCount} row(s) need qty/price{isDirectGrn && "/expiry"}
                </span>
              )}
            </div>

            {/* Bulk toolbar */}
            <div className="flex flex-wrap gap-2 mb-2">
              <Calendar
                placeholder="Set expiry for selected…"
                showIcon
                dateFormat="yy-mm-dd"
                minDate={minExpiryDate}
                onChange={(e) => e.value && setExpiryForSelected(e.value)}
                style={{ minWidth: '140px' }}
              />
              <Button label="Reapply Prices" icon="pi pi-refresh" onClick={reapplyPricesForSelected} outlined />
              <Button label="Qty = ROL" icon="pi pi-sort-amount-up" onClick={setQtyEqualROLForSelected} outlined />
              <Button label="Clear Qty" icon="pi pi-eraser" onClick={clearQtyForSelected} outlined />
              <span className="flex-1" />
              <Button label="Exit Fill" icon="pi pi-times" className="p-button-secondary" outlined onClick={() => setTemplateFillMode(false)} />
            </div>

            {/* Editable grid */}
            <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
              <DataTable value={items} showGridlines responsiveLayout="scroll" dataKey={(r) => `${r.product}`}
                         selection={selectedRowsQuick} selectionMode="checkbox"
                         onSelectionChange={(e) => setSelectedRowsQuick(e.value)}>
                <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>
                <Column field="product_name" header="Product" style={{ minWidth: 200 }} />
                <Column header="Mode" body={(row, opts) => (
                  <SelectButton
                    value={row.pricing_mode || 'pack'}
                    onChange={(e) => { if (e.value) updateItemField(opts.rowIndex, 'pricing_mode', e.value); }}
                    options={[{ label: 'Pack', value: 'pack' }, { label: 'Unit', value: 'unit' }]}
                    style={{ fontSize: '0.75rem' }}
                  />
                )} />
                <Column header="Qty (Packs/Units)" body={(row, opts) => (
                  row.pricing_mode === 'unit' ? (
                    <InputNumber
                      inputRef={getCellRef(opts.rowIndex, 'unit_qty')}
                      value={row.unit_qty}
                      min={0}
                      onValueChange={(e) => updateItemField(opts.rowIndex, 'unit_qty', e.value)}
                      onKeyDown={(e) => onCellKeyDown(e, opts.rowIndex, 'unit_qty', row)}
                    />
                  ) : (
                    <InputNumber
                      inputRef={getCellRef(opts.rowIndex, 'pack_quantity')}
                      value={row.pack_quantity}
                      min={0}
                      onValueChange={(e) => updateItemField(opts.rowIndex, 'pack_quantity', e.value)}
                      onKeyDown={(e) => onCellKeyDown(e, opts.rowIndex, 'pack_quantity', row)}
                    />
                  )
                )} />

                <Column header="Units per Pack" body={(row, opts) => (
                  row.pricing_mode === 'unit' ? <span style={{ opacity: .5 }}>—</span> : (
                    <InputNumber
                      inputRef={getCellRef(opts.rowIndex, 'units_per_pack')}
                      value={row.units_per_pack}
                      min={1}
                      onValueChange={(e) => updateItemField(opts.rowIndex, 'units_per_pack', e.value)}
                      onKeyDown={(e) => onCellKeyDown(e, opts.rowIndex, 'units_per_pack', row)}
                    />
                  )
                )} />

                <Column header="Price (Pack/Unit)" body={(row, opts) => (
                  row.pricing_mode === 'unit' ? (
                    <InputNumber
                      inputRef={getCellRef(opts.rowIndex, 'unit_price_input')}
                      value={row.unit_price_input}
                      min={0}
                      mode="currency"
                      currency="LKR"
                      onValueChange={(e) => updateItemField(opts.rowIndex, 'unit_price_input', e.value)}
                      onKeyDown={(e) => onCellKeyDown(e, opts.rowIndex, 'unit_price_input', row)}
                    />
                  ) : (
                    <InputNumber
                      inputRef={getCellRef(opts.rowIndex, 'pack_price')}
                      value={row.pack_price}
                      min={0}
                      mode="currency"
                      currency="LKR"
                      onValueChange={(e) => updateItemField(opts.rowIndex, 'pack_price', e.value)}
                      onKeyDown={(e) => onCellKeyDown(e, opts.rowIndex, 'pack_price', row)}
                    />
                  )
                )} />

                <Column header="ROL (Packs/Units)" body={(row, opts) => (
                  row.pricing_mode === 'unit' ? (
                    <InputNumber
                      inputRef={getCellRef(opts.rowIndex, 'unit_rol')}
                      value={row.unit_rol}
                      min={0}
                      onValueChange={(e) => updateItemField(opts.rowIndex, 'unit_rol', e.value)}
                      onKeyDown={(e) => onCellKeyDown(e, opts.rowIndex, 'unit_rol', row)}
                    />
                  ) : (
                    <InputNumber
                      inputRef={getCellRef(opts.rowIndex, 'rol_pack')}
                      value={row.rol_pack}
                      min={0}
                      onValueChange={(e) => updateItemField(opts.rowIndex, 'rol_pack', e.value)}
                      onKeyDown={(e) => onCellKeyDown(e, opts.rowIndex, 'rol_pack', row)}
                    />
                  )
                )} />

                <Column header="Expiry" body={(row, opts) => (
                  row.is_expiring ? (
                    <ExpiryInput
                      inputRef={getCellRef(opts.rowIndex, 'expiry_date')}
                      value={row.expiry_date}
                      onChange={(d) => {
                        if (d) {
                          updateItemField(opts.rowIndex, 'expiry_date', d);
                          focusField(opts.rowIndex, 'selling_price');
                        }
                      }}
                      onCommit={(d) => {
                        if (d) updateItemField(opts.rowIndex, 'expiry_date', d);
                      }}
                      onKeyDown={(e) => onCellKeyDown(e, opts.rowIndex, 'expiry_date', row)}
                      minDate={minExpiryDate}
                      style={{ minWidth: '140px', width: '100%' }}
                    />
                  ) : <span style={{ opacity: .6 }}>—</span>
                )} />  
                
                <Column header="Selling" body={(row, opts) => (
                  <InputNumber
                    inputRef={getCellRef(opts.rowIndex, 'selling_price')}
                    value={row.selling_price}
                    min={0}
                    mode="currency"
                    currency="LKR"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    onValueChange={(e) => updateItemField(opts.rowIndex, 'selling_price', e.value)}
                    onKeyDown={(e) => onCellKeyDown(e, opts.rowIndex, 'selling_price', row)}
                  />
                )} />

                <Column header="Discount (%)" body={(row, opts) => (
                  <InputNumber
                    inputRef={getCellRef(opts.rowIndex, 'purchase_discount')}
                    value={row.purchase_discount ?? 0}
                    min={0}
                    max={100}
                    suffix="%"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    onValueChange={(e) => updateItemField(opts.rowIndex, 'purchase_discount', e.value ?? 0)}
                    onKeyDown={(e) => onCellKeyDown(e, opts.rowIndex, 'purchase_discount', row)}
                  />
                )} />

                <Column header="Free (Packs/Units)" body={(row, opts) => (
                  row.pricing_mode === 'unit' ? (
                    <InputNumber
                      inputRef={getCellRef(opts.rowIndex, 'unit_free_qty')}
                      value={row.unit_free_qty || 0}
                      min={0}
                      onValueChange={(e) => updateItemField(opts.rowIndex, 'unit_free_qty', e.value || 0)}
                      onKeyDown={(e) => onCellKeyDown(e, opts.rowIndex, 'unit_free_qty', row)}
                    />
                  ) : (
                    <InputNumber
                      inputRef={getCellRef(opts.rowIndex, 'free_pack_quantity')}
                      value={row.free_pack_quantity || 0}
                      min={0}
                      onValueChange={(e) => updateItemField(opts.rowIndex, 'free_pack_quantity', e.value || 0)}
                      onKeyDown={(e) => onCellKeyDown(e, opts.rowIndex, 'free_pack_quantity', row)}
                    />
                  )
                )} />   
                
                <Column header="Batch" body={(row) => <span>{row.batch_number || ''}</span>} />
              </DataTable>
            </div>

            <div className="flex gap-2 mt-3">
              <Button label="Apply" icon="pi pi-check" onClick={() => setTemplateFillMode(false)} outlined disabled={items.length === 0 || incompleteCount > 0} />
              <Button label="Cancel" icon="pi pi-times" onClick={onHide} outlined />
            </div>
          </>
        ) : (
          /* CLASSIC SINGLE-ADD MODE */
          <>
            {editingIndex !== null && (
              <div className="p-2 mb-2" style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6 }}>
                Editing selected row — make changes and click <b>Update Item</b> or <b>Cancel Edit</b>.
              </div>
            )}

            <div className="formgrid grid">
              <div className="field col">
                <label htmlFor="product" className="font-bold">Product:</label>
                <div className="p-inputgroup">
                  <ProductSelectorInput
                    ref={productRef}
                    query={productQuery}
                    onQueryChange={setProductQuery}
                    onSelect={val => { setSelectedProduct(val); if (val) { fetchProductPrices(val.id); focusRef(qtyRef); } }}
                    placeholder="Select product"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focusRef(qtyRef); } }}
                    isdisabled={isReadOnlyOrder}
                  />
                  <span
                    className='p-inputgroup-addon'
                    onClick={() => { if (!isReadOnlyOrder) setScannerVisible(true); }}
                    style={{ cursor: isReadOnlyOrder ? 'not-allowed' : 'pointer', opacity: isReadOnlyOrder ? 0.5 : 1 }}
                  >
                    <FaBarcode size={20} title="Scan Barcode" />
                  </span>
                </div>
              </div>
              <div className="field col">
                <label className="font-bold">Pricing Mode:</label>
                <SelectButton
                  disabled={isReadOnlyOrder || unitStocksMode}
                  value={pricingMode}
                  onChange={(e) => { if (e.value) switchPricingMode(e.value); }}
                  options={[
                    { label: 'Pack', value: 'pack' },
                    { label: 'Unit', value: 'unit' },
                  ]}
                />
              </div>
              <div className="field col">
                <label htmlFor="quantity" className="font-bold">{pricingMode === 'unit' ? 'Quantity (Units):' : 'No of Packs:'}</label>
                <InputNumber id='quantity' disabled={isReadOnlyOrder} inputRef={qtyRef} value={qty} onValueChange={e => {
                      setQty(e.value);
                      if (pricingMode === 'pack') {
                        const computed = computeFromPack(e.value, unitsPerPack, packPrice, freeQty, purchaseDiscount);
                        setLineTotalCostComputed(computed.line_total_cost);
                        setPurchasePrice(computed.purchase_price);
                        setUnitPriceUI((unitsPerPack && packPrice) ? (packPrice / unitsPerPack) : null);
                      } else {
                        const computedLine = computeLineTotal(unitPriceUI, e.value, purchaseDiscount);
                        setLineTotalCostComputed(computedLine);
                        const computedPurchase = computePurchasePrice(computedLine, e.value, freeQty);
                        setPurchasePrice(computedPurchase);
                      }
                    }} placeholder={pricingMode === 'unit' ? 'Quantity' : 'Quantity'} min={1}
                             onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focusRef(pricingMode === 'unit' ? rlRef : uppRef); } }} />
              </div>
              {pricingMode === 'pack' && (
              <div className="field col">
                <label className="font-bold">Units per Pack:</label>
                <InputNumber
                  inputRef={uppRef}
                  value={unitsPerPack}
                  onValueChange={(e) => setUnitsPerPack(e.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focusRef(rlRef); } }} 
                  placeholder="e.g. 10 tablets per pack"
                  min={1}
                  disabled={isReadOnlyOrder}
                />
              </div>
              )}
              <div className="field col">
                <label htmlFor="rol" className="font-bold">{pricingMode === 'unit' ? 'Reorder Level (Units):' : 'ROL (Packs):'}</label>
                <InputNumber id='rol' disabled={isReadOnlyOrder} inputRef={rlRef} value={rl} onValueChange={e => {const packRol = e.value;  setRl(packRol);}} placeholder="Reorder Level"
                             onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (selectedProduct?.is_expiring) {focusRef(expiryRef);} else {focusRef(unitPriceRef);} } }} />
              </div>
              <div className="field col">
                <label htmlFor="expiry_date" className="font-bold">Expiry Date:</label>
                <ExpiryInput
                  value={expiryDate}
                  onChange={(d) => setExpiryDate(d)}
                  onCommit={(d) => { if (d) focusRef(unitPriceRef); }}
                  disabled={isReadOnlyOrder || !selectedProduct?.is_expiring}
                  minDate={minExpiryDate}
                  inputRef={expiryRef}
                  style={{ minWidth: '140px', width: '100%' }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focusRef(unitPriceRef); } }}
                />
              </div>
              {pricingMode === 'unit' ? (
              <div className="field col">
                <label htmlFor="unitPrice" className="font-bold">Unit Price:</label>
                <InputNumber id='unitPrice' disabled={isReadOnlyOrder} inputRef={unitPriceRef} value={unitPriceUI} onValueChange={e => {
                      setUnitPriceUI(e.value);
                      const computedLine = computeLineTotal(e.value, qty, purchaseDiscount);
                      setLineTotalCostComputed(computedLine);
                      const computedPurchase = computePurchasePrice(computedLine, qty, freeQty);
                      setPurchasePrice(computedPurchase);
                    }} placeholder="Enter price per unit" min={0} mode="currency" currency="LKR"
                    onKeyDown={(e) => blurOnEnter(e, sellingPriceRef)} />
              </div>
              ) : (
              <div className="field col">
                <label htmlFor="unitPrice" className="font-bold">Pack Price:</label>
                <InputNumber id='unitPrice' disabled={isReadOnlyOrder} inputRef={unitPriceRef} value={packPrice} onValueChange={e => {
                      setPackPrice(e.value);
                      if (qty && unitsPerPack) {
                        const computed = computeFromPack(qty, unitsPerPack, e.value, freeQty, purchaseDiscount);
                        setLineTotalCostComputed(computed.line_total_cost);
                        setPurchasePrice(computed.purchase_price);
                      }
                    }} placeholder="Enter pack price" min={0} mode="currency" currency="LKR"
                    onKeyDown={(e) => blurOnEnter(e, sellingPriceRef)} />
              </div>
              )}
              <div className="field col">
                <label className="font-bold">Selling Price:</label>
                <InputNumber disabled={isReadOnlyOrder} inputRef={sellingPriceRef} value={sellingPrice} onValueChange={e => setSellingPrice(e.value)} min={0}
                             placeholder="Selling Price" mode="currency" currency="LKR" onKeyDown={(e) => blurOnEnter(e, freeQtyRef)} />
              </div>
              <div className="field col">
                <label htmlFor="freeQty" className="font-bold">{pricingMode === 'unit' ? 'Free Qty (Units):' : 'Free Packs:'}</label>
                <InputNumber id='freeQty' disabled={isReadOnlyOrder} inputRef={freeQtyRef} value={freeQty} onValueChange={e => {
                      setFreeQty(e.value || 0);
                      if (pricingMode === 'pack') {
                        const computed = computeFromPack(qty, unitsPerPack, packPrice, e.value || 0, purchaseDiscount);
                        setLineTotalCostComputed(computed.line_total_cost);
                        setPurchasePrice(computed.purchase_price);
                        setUnitPriceUI((unitsPerPack && packPrice) ? (packPrice / unitsPerPack) : null);
                      } else {
                        const computedLine = computeLineTotal(unitPriceUI, qty, purchaseDiscount);
                        setLineTotalCostComputed(computedLine);
                        const computedPurchase = computePurchasePrice(computedLine, qty, e.value || 0);
                        setPurchasePrice(computedPurchase);
                      }
                    }} placeholder="0" min={0}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      focusAny(discountRef);
                    }}
                />
              </div>
              <div className="field col">
                <label htmlFor="purchaseDiscount" className="font-bold">Discount (%):</label>
                <InputNumber
                  id='purchaseDiscount'
                  disabled={isReadOnlyOrder}
                  inputRef={discountRef}
                  value={purchaseDiscount}
                  onValueChange={e => {
                    const disc = e.value ?? 0;
                    setPurchaseDiscount(disc);
                    if (pricingMode === 'pack') {
                      const computed = computeFromPack(qty, unitsPerPack, packPrice, freeQty, disc);
                      setLineTotalCostComputed(computed.line_total_cost);
                      setPurchasePrice(computed.purchase_price);
                    } else {
                      const computedLine = computeLineTotal(unitPriceUI, qty, disc);
                      setLineTotalCostComputed(computedLine);
                      const computedPurchase = computePurchasePrice(computedLine, qty, freeQty);
                      setPurchasePrice(computedPurchase);
                    }
                  }}
                  placeholder="0"
                  min={0}
                  max={100}
                  suffix="%"
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    if (editingIndex === null) focusAny(addRef);
                    else focusAny(updateRef);
                  }}
                />
              </div>
              <div className="field col">
                <label htmlFor="lineTotalComputed" className="font-bold">Line Total:</label>
                <InputNumber id='lineTotalComputed' value={lineTotalCostComputed} placeholder="Computed" mode="currency" currency="LKR" disabled />
              </div>
              <div className="field col">
                <label htmlFor="purchasePrice" className="font-bold">Purchase Price:</label>
                <InputNumber id='purchasePrice' value={purchasePrice} placeholder="Computed" mode="currency" currency="LKR" disabled />
              </div>
              <div className="field col">
                <label className="font-bold">Batch Number:</label>
                <InputText value={batchNumber} disabled placeholder="Batch Number (auto)" style={{ backgroundColor: '#e9ecef' }} />
                <span style={{ fontSize: '11px', color: '#888' }}>Auto-generated</span>
              </div>
            </div>
            <div className="flex flex-wrap align-items-center gap-2 sm:gap-2" style={{ margin: '10px' }}>
              {editingIndex === null ? (
                <Button ref={addRef} label="Add Item" icon="pi pi-plus" onClick={addItem}
                        disabled={isReadOnlyOrder || !selectedProduct || !qty || !purchasePrice || !sellingPrice || (isDirectGrn && selectedProduct.is_expiring && !expiryDate)}
                        style={{ borderRadius: '6px', fontSize: '14px' }} outlined size="medium" />
              ) : (
                <>
                  <Button ref={updateRef} label="Update Item" icon="pi pi-refresh" onClick={updateItem}
                          disabled={isReadOnlyOrder || !selectedProduct || !qty || !purchasePrice || !sellingPrice || (isDirectGrn && selectedProduct.is_expiring && !expiryDate)}
                          style={{ borderRadius: '6px', fontSize: '14px' }} outlined size="medium" />
                  <Button label="Cancel Edit" icon="pi pi-undo" onClick={() => { resetForm(); setSelectedRow(null); setEditingIndex(null); }}
                          className="p-button-secondary" outlined style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" />
                </>
              )}
            </div>

            <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
              <DataTable value={items} showGridlines responsiveLayout="stack" selectionMode={!isReadOnlyOrder ? "single":null} selection={selectedRow}
                         onSelectionChange={onSelectionChange} dataKey={(row) => `${row.product}-${row.batch_number}`}>
                <Column field="product_name" header="Product" />
                <Column header="Type" body={(row) => (
                  <Tag value={row.units_per_pack ? 'Pack' : 'Unit'} severity={row.units_per_pack ? 'info' : 'warning'} />
                )} />
                <Column header="Qty" body={(row) => (
                  row.units_per_pack ? <span>{row.pack_quantity} <span style={{ opacity: .6 }}>(P)</span></span> : <span>{row.quantity} <span style={{ opacity: .6 }}>(U)</span></span>
                )} />
                <Column header="Free" body={(row) => (
                  row.units_per_pack
                    ? <span>{row.free_quantity / row.units_per_pack} <span style={{ opacity: .6 }}>(P)</span></span>
                    : <span>{row.free_quantity} <span style={{ opacity: .6 }}>(U)</span></span>
                )} />
                <Column header="Total Units" body={(row) => (row.quantity || 0) + (row.free_quantity || 0)} />
                <Column header="ROL" body={(row) => (
                  row.units_per_pack
                    ? <span>{row.reorder_level / row.units_per_pack} <span style={{ opacity: .6 }}>(P)</span></span>
                    : <span>{row.reorder_level} <span style={{ opacity: .6 }}>(U)</span></span>
                )} />             
                <Column header="Unit Price" body={(row) => <span>{row.unit_price_ui != null ? Number(row.unit_price_ui).toFixed(2) : ''}</span>} />
                <Column header="Purchase Price" body={(row) => <span>{row.purchase_price != null ? Number(row.purchase_price).toFixed(2) : ''}</span>} />               
                <Column header="Discount" body={(row) => <span>{row.purchase_discount != null && row.purchase_discount !== 0 ? `${Number(row.purchase_discount).toFixed(2)}%` : '—'}</span>} />
                <Column header="Selling Price" body={(row) => <span>{row.selling_price != null ? Number(row.selling_price).toFixed(2) : ''}</span>} />
                <Column header="Total" body={(row) => <span>{row.line_total_cost != null ? Number(row.line_total_cost).toFixed(2) : ''}</span>} />
                <Column field="expiry_date" header="Expiry Date" />
                <Column field="batch_number" header="Batch Number" />
                {!isReadOnlyOrder && (
                <Column header="" body={row => (
                  <Button icon="pi pi-trash" className="p-button-text p-button-danger"
                          onClick={(e) => { e.stopPropagation?.(); removeItem(row.product, row.batch_number); }} />
                )} style={{ width: '3rem' }} /> )}
              </DataTable>
            </div>
          </>
        )}

        {/* Barcode modal */}
        <Dialog header="Scan Barcode" visible={scannerVisible} onHide={() => setScannerVisible(false)} modal style={{ width: 380 }}>
          <video id="move-order-barcode-video" width="100%" height="250" />
        </Dialog>

        {/* Create GRN confirmation dialog */}
        <Dialog header="Create GRN" visible={grnDialogVisible} onHide={() => setGrnDialogVisible(false)} modal closable={true} style={{ width: '420px' }}>
          <div className="p-fluid">
            <div className="field">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Checkbox inputId="grnReceiveOnCredit" checked={grnReceiveOnCredit} onChange={(e) => setGrnReceiveOnCredit(!!e.checked)} />
                <label htmlFor="grnReceiveOnCredit" style={{ margin: 0 }}>Receive on Credit</label>
              </div>
            </div>

            <div className="field">
              <label htmlFor="grnSupplier" className="font-bold">Supplier</label>
              <Dropdown
                inputId="grnSupplier"
                value={grnSelectedSupplier}
                options={suppliers}
                onChange={(e) => setGrnSelectedSupplier(e.value)}
                optionLabel="name"
                optionValue="id"
                placeholder="Select Supplier"
                filter
                disabled={!!currentOrder} // follow main dialog behavior
              />
            </div>

            <div className="field">
              <label htmlFor="grnInvoice" className="font-bold">Supplier Invoice Number</label>
              <InputText id="grnInvoice" value={grnSupplierInvoiceNo} onChange={(e) => setGrnSupplierInvoiceNo(e.target.value)} placeholder="Enter supplier invoice number" />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <Button label="Cancel" onClick={() => setGrnDialogVisible(false)} className="p-button-secondary" />
              <Button label="Create" onClick={handleCreateGrnSubmit} loading={grnSubmitting} />
            </div>
          </div>
        </Dialog>

        {/* Send to Supplier dialog */}
        <Dialog header="Send PO to Supplier" visible={sendDialogVisible} onHide={() => setSendDialogVisible(false)} modal style={{ width: 520 }}>
          <div className="p-fluid">
            <div className="field">
              <label>Supplier Email</label>
              <InputText value={sendEmailTo || supplierEmail || ''} onChange={(e) => setSendEmailTo(e.target.value)} placeholder="supplier@example.com" />
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button label="Cancel" className="p-button-secondary" onClick={() => setSendDialogVisible(false)} outlined />
              <Button label="Send" icon="pi pi-envelope" onClick={async () => {
                await sendToSupplier(sendEmailTo || null);
                setSendDialogVisible(false);
              }} />
            </div>
          </div>
        </Dialog>

        {/* Receive Goods dialog */}
        <Dialog header="Receive Goods" visible={receiveDialogVisible} onHide={() => setReceiveDialogVisible(false)} modal style={{ width: '70vw' }}>
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <DataTable value={receiveItemsState} responsiveLayout="scroll">
              <Column header="Product" body={(r) => <div>{r.product_name}</div>} />
              <Column header="Ordered" body={(r) => <div>{r.ordered_quantity} {r.units_per_pack ? 'packs' : 'units'}</div>} />
              <Column header="Receive Qty" body={(r, opts) => (
                currentOrder?.is_direct_grn ? (
                  <div>{r.received_quantity} {r.units_per_pack ? 'packs' : 'units'}</div>
                ) : (
                  <InputNumber value={r.received_quantity} min={0} suffix={r.units_per_pack ? ' packs' : ' units'} onValueChange={(e) => {
                    const nv = [...receiveItemsState]; nv[opts.rowIndex] = { ...nv[opts.rowIndex], received_quantity: e.value || 0 }; setReceiveItemsState(nv);
                  }} />
                )
              )} />
              <Column header="Free Qty" body={(r, opts) => (
                currentOrder?.is_direct_grn ? (
                  <div>{r.free_quantity || 0} {r.units_per_pack ? 'packs' : 'units'}</div>
                ) : (
                  <InputNumber value={r.free_quantity || 0} min={0} suffix={r.units_per_pack ? ' packs' : ' units'} onValueChange={(e) => {
                      const nv = [...receiveItemsState]; nv[opts.rowIndex] = { ...nv[opts.rowIndex], free_quantity: e.value || 0,}; setReceiveItemsState(nv);
                    }} />
                )
              )} />
              <Column header="Expiry" body={(r, opts) => (
                currentOrder?.is_direct_grn ? (
                  <div>{toYmd(r.expiry_date) || '—'}</div>
                ) : (
                  <Calendar value={r.expiry_date} dateFormat="yy-mm-dd" showIcon onChange={(e) => { const nv = [...receiveItemsState]; nv[opts.rowIndex] = { ...nv[opts.rowIndex], expiry_date: e.value }; setReceiveItemsState(nv); }} minDate={minExpiryDate} style={{ minWidth: '140px' }}/>
                )
              )} />
            </DataTable>
          </div>
          <div className="p-fluid mt-3">
            <div className="field">
                  <Checkbox inputId="oncredit" checked={receiveOnCredit} onChange={(e) => setReceiveOnCredit(e.checked)} />
                  <label htmlFor="oncredit" style={{ marginLeft: 8 }}>Receive on credit (create supplier invoice)</label>
                </div>
                {receiveOnCredit && (
                  <>
                    <div className="field">
                      <label>Supplier</label>
                      <Dropdown value={receiveSupplierId} options={suppliers} optionLabel="name" optionValue="id"
                                onChange={(e) => setReceiveSupplierId(e.value)} placeholder="Select supplier" filter />
                    </div>
                    <div className="field">
                      <label>Supplier invoice number (required)</label>
                      <InputText value={receiveInvoiceNo} onChange={(e) => setReceiveInvoiceNo(e.target.value)} />
                    </div>
                  </>
                )}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button label="Cancel" className="p-button-secondary" onClick={() => setReceiveDialogVisible(false)} outlined />
            <Button label="Receive" icon="pi pi-download" onClick={async () => {
              // call receiveGoods helper with collected payload
              const missingExpiry = (receiveItemsState || []).filter(
                r => r.is_expiring && !r.expiry_date
              );
              if (missingExpiry.length > 0) {
                handleApiError({
                  Error: 'Please enter expiry date for all expiring products before receiving.',
                });
                return;
              }
              await receiveGoods({ receive_on_credit: receiveOnCredit, supplier_invoice_number: receiveInvoiceNo, itemsPayload: receiveItemsState });
              setReceiveDialogVisible(false);
            }} />
          </div>
        </Dialog>

        <RecordPaymentDialog
          visible={paymentDialogVisible}
          onHide={() => setPaymentDialogVisible(false)}
          order={currentOrder}
          onSuccess={() => {
            setPaymentDialogVisible(false);
            if (currentOrder?.id) refreshCurrentOrder(currentOrder.id);
            onPurchased?.();
          }}
        />

        {/* Template dialogs */}
        <PurchaseTemplateSaveDialog visible={templateSaveVisible} onHide={() => setTemplateSaveVisible(false)} onSave={onSaveTemplate} suppliers={suppliers} />
        <PurchaseTemplateLoadDialog visible={templateLoadVisible} onHide={() => setTemplateLoadVisible(false)} onLoaded={onLoadTemplateChosen} />
      </div>
    </Dialog>
  );
}

/** Save Template Dialog */
export function PurchaseTemplateSaveDialog({ visible, onHide, onSave, suppliers = [] }) {
  const [name, setName] = useState('');
  const [supplierId, setSupplierId] = useState(null);

  useEffect(() => { if (!visible) { setName(''); setSupplierId(null); } }, [visible]);

  return (
    <Dialog header="Save as Template" visible={visible} onHide={onHide} modal style={{ width: 420 }}>
      <div className="flex flex-column gap-3">
        <div
          className="flex flex-column gap-4"
          style={{
            padding: '1.5rem 1rem',
            borderRadius: '8px',
            background: '#f8f9fa',
            border: '1px solid #e0e0e0',
          }}
        >
        <span className="p-float-label">
          <InputText id="tplname" value={name} onChange={(e) => setName(e.target.value)} autoFocus/>
          <label htmlFor="tplname">Template Name</label>
        </span>
        <span className="p-float-label">
          <Dropdown inputId="tplsupplier" value={supplierId} onChange={(e) => setSupplierId(e.value)} options={suppliers}
                    optionLabel="name" optionValue="id" placeholder="Select Supplier" filter />
          <label htmlFor="tplsupplier">Supplier</label>
        </span>
        </div>
        <div className="flex gap-2 justify-end mt-2">
          <Button label="Cancel" className="p-button-secondary" outlined onClick={onHide} />
          <Button label="Save" icon="pi pi-save" onClick={() => onSave({ name, supplierId })} />
        </div>
      </div>
    </Dialog>
  );
}

/** Load Template Dialog */
export function PurchaseTemplateLoadDialog({ visible, onHide, onLoaded }) {
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!visible) { setRows([]); setSelected(null); setQ(''); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get('/api/purchase-templates/', { params: q ? { q } : {} });
        setRows(res.data || []);
      } catch (e) { handleApiError(e); }
      finally { setLoading(false); }
    })();
  }, [visible, q]);

  return (
    <Dialog header="Load Template" visible={visible} onHide={onHide} modal style={{ width: 600 }}>
      <div className="flex items-center gap-2 mb-2">
        <IconField iconPosition="left" className="w-full sm:w-auto">
            <InputIcon className="pi pi-search" />
            <InputText value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates…" className="w-full sm:w-auto" />
        </IconField> 
        <Button icon="pi pi-refresh" outlined onClick={() => setQ(q => q)} />
      </div>
      <DataTable value={rows} loading={loading} paginator rows={8} selectionMode="single" selection={selected}
                 onSelectionChange={(e) => setSelected(e.value)} dataKey={(r) => r.id} responsiveLayout="scroll">
        <Column field="name" header="Name" />
        <Column field="supplier_name" header="Supplier" />
        <Column header="Updated" body={(row) => {
            if (!row.updated_at) return "-";
            return format(new Date(row.updated_at), 'Pp');
          }}/>
        <Column header="" style={{ width: '3rem', textAlign: 'center' }}
          body={(row) => (
            <Button icon="pi pi-trash" className="p-button-text p-button-danger" 
              onClick={async (e) => {
                e.stopPropagation();

                if (!window.confirm(`Delete template "${row.name}"?`)) return;

                try {
                  await axiosInstance.delete(`/api/purchase-templates/${row.id}/`);
                  handleApiSuccess("Template deleted");
                  // Refresh list
                  setRows(prev => prev.filter(r => r.id !== row.id));
                  if (selected?.id === row.id) setSelected(null);
                } catch (err) {
                  handleApiError(err);
                }
              }}
            />
          )}
        />
      </DataTable>
      <div className="flex justify-end gap-2 mt-3">
        <Button label="Cancel" className="p-button-secondary" outlined onClick={onHide} />
        <Button label="Load" icon="pi pi-upload" disabled={!selected} onClick={() => onLoaded(selected)} />
      </div>
    </Dialog>
  );
}